import { useGetAccountsQuery } from "@/modules/account/services";
import type {
  ICurrencyExchangeTemplateForm,
  ICurrencyExchangeTemplateRequest
} from "@/modules/template/common/types";
import {
  useCreateCurrencyExchangeTemplateMutation,
  useGetCurrencyExchangeTemplateQuery,
  useUpdateCurrencyExchangeTemplateMutation,
} from "@/modules/template/services";
import { CurrencyExchangeKeys, TransferKeys } from "@/modules/transaction/common/enum";
import type { ICurrencyExchangeCalculation } from "@/modules/transaction/common/types";
import { useCurrencyExchange } from "@/modules/transaction/hooks";
import { useLazyGetExchangeRatePersonalizedQuery } from "@/modules/transaction/services";
import { CURRENCY, INITIAL_PAGE_SIZE, INPUT_MAX_LIMIT, } from "@/shared/common/constants";
import { CloudflareAsset, SearchParamKeys } from "@/shared/common/enums";
import type { IExtendedObject } from "@/shared/common/types";
import {
  CloudFlareImage,
  OwnAccountSelectOption,
  Select,
  TemplateLayout,
} from "@/shared/components";
import { cleanupAmount, getAmountOrNull, isValidAmount, omitKey } from "@/shared/helpers";
import { useHasFormChanges } from "@/shared/hooks";
import { CollapseGroup } from "@ab.uitools/ui-library/components/Collapse";
import { FormField } from "@ab.uitools/ui-library/components/FormField";
import { Image } from "@ab.uitools/ui-library/components/Image";
import { Input } from "@ab.uitools/ui-library/components/Input";
import { Text } from "@ab.uitools/ui-library/components/Text";
import { Textarea } from "@ab.uitools/ui-library/components/Textarea";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useSearchParams } from "react-router-dom";
import uuid4 from "uuid4";

interface Props {
  isEdit?: boolean;
  isCopy?: boolean;
}

export const CurrencyExchangeTemplateForm = ({ isEdit }: Props) => {
  const { t } = useTranslation();
  const { operationId = "" } = useParams();
  const [searchParams] = useSearchParams();
  const idempotencyKey = useRef(uuid4()).current;
  const templateName = searchParams.get(SearchParamKeys.TemplateName);

  const [
    createCurrencyExchangeTemplateApi,
    { isLoading: isCreateTemplateLoading },
  ] = useCreateCurrencyExchangeTemplateMutation();
  const [
    updateCurrencyExchangeTemplateApi,
    { isLoading: isUpdateTemplateLoading },
  ] = useUpdateCurrencyExchangeTemplateMutation();
  const [getPersonalizedRate, { data: rateInfo }] = useLazyGetExchangeRatePersonalizedQuery();

  const {
    data: currencyExchangeTemplate,
    isLoading: isGetTemplateLoading,
  } = useGetCurrencyExchangeTemplateQuery({ id: operationId }, {
    refetchOnMountOrArgChange: true,
    skip: !isEdit && !operationId
  });

  const {
    data: accountsList = [],
    isLoading: isGetAccountsLoading,
  } = useGetAccountsQuery({
    pageSize: INITIAL_PAGE_SIZE,
  }, {
    refetchOnMountOrArgChange: true,
  });

  const {
    reset,
    errors,
    values,
    setValue,
    creditAccount,
    debitAccount,
    creditAccountList,
    debitAccountList,
    setCreditAccountList,
    setDebitAccountList,
    creditAccountsFilteredList,
    debitAccountsFilteredList,
    bankRateHelperText,
  } = useCurrencyExchange({ accountsList, rateInfo });

  const [defaultData, setDefaultData] = useState<IExtendedObject>({});

  const currencyExchangeTemplateAction = useCallback((values: ICurrencyExchangeTemplateForm) => {
    const requestData: ICurrencyExchangeTemplateRequest & { wantAmount?: string } = omitKey({
      ...values,
      name: templateName ?? "",
      amount: values.amount || null,
      debitAccount,
      creditAccount,
    }, [CurrencyExchangeKeys.WantAmount]);

    return isEdit ?
      updateCurrencyExchangeTemplateApi({
        data: requestData,
        id: `${operationId}`
      }).unwrap() :
      createCurrencyExchangeTemplateApi({
        data: requestData,
        idempotencyKey,
      }).unwrap();
  }, [accountsList, creditAccount, debitAccount]);

  const handleSwapCurrencies = () => {
    if (!values.creditAccount || !values.debitAccount) return;
    const creditAccountsOptions = creditAccountList;
    setCreditAccountList(debitAccountList);
    setDebitAccountList(creditAccountsOptions);

    const creditAccountNumber = values.creditAccount;
    setValue(TransferKeys.CreditAccount, values.debitAccount);
    setValue(TransferKeys.DebitAccount, creditAccountNumber);
    setValue(CurrencyExchangeKeys.WantAmount, "");
  };

  const getCalculatedAmount = ({
    amount,
    rate,
    isHaveAmount,
    rateCurrency,
  }: ICurrencyExchangeCalculation) => {
    const currentRate = rate || rateInfo?.rate;
    const currency = rateCurrency ?? rateInfo?.currency;
    const sameCurrency = currency === (isHaveAmount ? debitAccount?.currency : creditAccount?.currency);
    return sameCurrency ?
      +cleanupAmount(amount) / +cleanupAmount(`${currentRate}`) :
      +cleanupAmount(amount) * +cleanupAmount(`${currentRate}`);
  };

  const handleCalculateWantAmount = ({
    amount,
    rate,
    rateCurrency,
  }: ICurrencyExchangeCalculation) => {
    const currentRate = rate || values.rate;
    if (currentRate) {
      const calculatedAmount = getCalculatedAmount({
        amount,
        rate: currentRate,
        isHaveAmount: true,
        rateCurrency,
      });
      setValue(
        CurrencyExchangeKeys.WantAmount,
        calculatedAmount ? `${calculatedAmount.toFixed(2)}` : "",
        { shouldValidate: true }
      );
    }
  };

  const handleCalculateHaveAmount = ({ amount, rate }: ICurrencyExchangeCalculation) => {
    setValue(CurrencyExchangeKeys.WantAmount, amount);
    const currentRate = rate || values.rate;
    if (currentRate) {
      const calculatedAmount = getCalculatedAmount({ amount });
      setValue(
        TransferKeys.Amount, calculatedAmount ? `${calculatedAmount.toFixed(2)}` : "",
        { shouldValidate: true }
      );
    }
  };

  const setDefaultValues = () => {
    const data = {
      creditAccount: currencyExchangeTemplate?.creditAccount?.number,
      debitAccount: currencyExchangeTemplate?.debitAccount?.number,
      amount: getAmountOrNull(currencyExchangeTemplate?.amount),
      purpose: currencyExchangeTemplate?.purpose,
      currency: currencyExchangeTemplate?.currency,
    };
    handleCalculateWantAmount({ amount: `${data.amount}` });
    reset?.(data);
    setDefaultData(data);
  };

  const currentData = useMemo(() => ({
    creditAccount: values.creditAccount,
    debitAccount: values.debitAccount,
    amount: getAmountOrNull(values.amount),
    purpose: values.purpose,
    currency: values.currency,
  }), [values]);

  const hasChanges = useHasFormChanges(defaultData, currentData);

  useEffect(() => {
    if (
      values.creditAccount &&
      values.debitAccount &&
      creditAccount &&
      debitAccount
    ) {
      const creditAccountCurrency = creditAccount?.currency as string;
      const debitAccountCurrency = debitAccount?.currency as string;
      if (creditAccountCurrency === debitAccountCurrency) return;
      getPersonalizedRate({
        buyCurrency: creditAccountCurrency,
        sellCurrency: debitAccountCurrency,
      }).unwrap()
        .then((data) => {
          setValue(CurrencyExchangeKeys.Rate, data.rate);
          if (values.amount) {
            handleCalculateWantAmount({
              amount: values.amount, rate: data.rate, rateCurrency: data.currency
            });
          } else if (values.wantAmount) {
            handleCalculateHaveAmount({ amount: values.wantAmount, rate: data.rate });
          }
        });
    }
  }, [
    values.creditAccount,
    values.debitAccount,
    creditAccount?.currency,
    debitAccount?.currency,
  ]);

  useEffect(() => {
    if (isEdit && !!currencyExchangeTemplate?.transactionType) {
      setDefaultValues();
    }
  }, [isEdit, currencyExchangeTemplate]);

  useEffect(() => {
    if (!isEdit) {
      setValue(TransferKeys.Purpose, t("transfer.currencyExchange.purpose"));
    }
  }, [isEdit]);

  return (
    <TemplateLayout
      title={t("template.currencyExchange.title")}
      page="Currency exchange template"
      className="currency-exchange-form"
      isLoading={isCreateTemplateLoading || isUpdateTemplateLoading}
      isEdit={isEdit}
      hasChanges={hasChanges}
      isGetTemplateLoading={isGetTemplateLoading}
      accountsList={accountsList}
      saveTemplate={currencyExchangeTemplateAction}
      withCommission={false}
    >
      <CollapseGroup
        className="collapse-group"
        items={[
          {
            content: <>
              <FormField
                name="debitAccount"
                className="mb-32"
                shouldValidate
                errorMessageIcon="infoFilled"
                As={(props) => (
                  <Select
                    {...props}
                    required
                    isSearchable
                    isClearable
                    className="custom-select"
                    options={debitAccountsFilteredList}
                    isDisabled={isGetAccountsLoading}
                    label={t("transfer.currencyExchange.fromAccount")}
                    placeholder={t("transfer.currencyExchange.chooseFromAccount")}
                    components={{ Option: OwnAccountSelectOption }}
                  />
                )}
              />
              <FormField
                name="creditAccount"
                className="mb-32"
                shouldValidate
                errorMessageIcon="infoFilled"
                As={(props) => (
                  <Select
                    {...props}
                    required
                    isSearchable
                    isClearable
                    className="custom-select"
                    options={creditAccountsFilteredList}
                    isDisabled={isGetAccountsLoading}
                    label={t("transfer.currencyExchange.toAccount")}
                    placeholder={t("transfer.currencyExchange.chooseToAccount")}
                    components={{ Option: OwnAccountSelectOption }}
                  />
                )}
              />
              {debitAccount?.currency && creditAccount?.currency && rateInfo?.currency && (
                <div className="widget p-16 mb-40">
                  <div
                    className="flexbox align-items--center p-4 gap-4 mb-16 currency-exchange-flag--wrapper">
                    <div className="flexbox align-items--center flag-container">
                      <Image
                        className="currency-exchange-flag--image"
                        imagePath={CURRENCY[debitAccount?.currency as keyof typeof CURRENCY]?.flag}
                      />
                      <Text className="pl-8">{debitAccount?.currency}</Text>
                    </div>
                    <div
                      className="flexbox flex-direction--column align-items--center fit-available">
                      <CloudFlareImage
                        className="switch-arrows cursor-pointer"
                        onClick={handleSwapCurrencies}
                        path={CloudflareAsset.CurrencyExchangeArrows}
                      />
                      <Text type="tertiary" className="mt-4">{bankRateHelperText}</Text>
                    </div>
                    <div className="flexbox align-items--center flag-container">
                      <Image
                        className="currency-exchange-flag--image"
                        imagePath={CURRENCY[creditAccount?.currency as keyof typeof CURRENCY]?.flag}
                      />
                      <Text className="pl-8">{creditAccount?.currency}</Text>
                    </div>
                  </div>
                  <div className="currency-exchange-amount-wrapper full-width">
                    <FormField
                      name="amount"
                      errorMessageIcon="infoFilled"
                      As={(props) => (
                        <Input
                          {...props}
                          type="numeric"
                          allowNegative={false}
                          allowLeadingZeros={false}
                          label={t("transfer.currencyExchange.iHave")}
                          handleChange={(_, val) => handleCalculateWantAmount({ amount: val })}
                          placeholder={debitAccount?.currency === CURRENCY.AMD.label ? "0.00" : "0.0"}
                          isAllowed={({ formattedValue }) => isValidAmount(formattedValue)}
                          thousandSeparator=","
                          decimalScale={debitAccount?.currency === CURRENCY.AMD.label ? 1 : 2}
                          rightIconProps={{
                            Component: CURRENCY[debitAccount?.currency as string]?.symbolIcon || "",
                          }}
                        />
                      )}
                    />
                    <FormField
                      name="wantAmount"
                      errorMessageIcon="infoFilled"
                      As={(props) => (
                        <Input
                          {...props}
                          type="numeric"
                          allowNegative={false}
                          allowLeadingZeros={false}
                          label={t("transfer.currencyExchange.iWant")}
                          handleChange={(_, val) => handleCalculateHaveAmount({ amount: val })}
                          placeholder={creditAccount?.currency === CURRENCY.AMD.label ? "0.00" : "0.0"}
                          thousandSeparator=","
                          decimalScale={creditAccount?.currency === CURRENCY.AMD.label ? 1 : 2}
                          rightIconProps={{
                            Component: CURRENCY[creditAccount?.currency as string]?.symbolIcon || "",
                          }}
                          isAllowed={({ formattedValue }) => isValidAmount(formattedValue)}
                        />
                      )}
                    />
                  </div>
                </div>
              )}
              <FormField
                name="purpose"
                className="mb-8"
                errorMessageIcon="infoFilled"
                hideErrorMessage
                As={(props) => (
                  <Textarea
                    {...props}
                    error={`${errors?.purpose?.message || ""}`}
                    label={t("general.purpose")}
                    placeHolder={t("general.enterYourPurpose")}
                    maxCount={INPUT_MAX_LIMIT}
                  />
                )}
              />
            </>,
            title: t("transfer.transferDetails"),
            value: "transfer_details",
            isOpen: true,
          },
        ]}
      />
    </TemplateLayout>
  );
};
